<template>
	<div class="modal fade show" tabindex="-1" role="dialog" style="display: block; background: rgba(0, 0, 0, 0.5);">
		<div class="modal-dialog modal-lg modal-fullscreen-sm-down" role="document">
			<div class="modal-content">
				<div class="modal-header bg-secondary text-white">
					<h5 class="modal-title">{{ title }}</h5>
					<button type="button" class="btn-close btn-close-white" @click="close()"></button>
				</div>

				<div class="modal-body">
					<!-- Step 1: scelta utente (solo admin) -->
					<div v-if="isAdminView && !selectedUtente" class="mb-3">
						<label class="form-label">Utente *</label>
						<select v-model="selectedUtente" class="form-control" required>
							<option value="">Seleziona...</option>
							<option v-for="u in $root.arrayUtentiAdmin" :key="u.ID_UTENTE" :value="u.ID_UTENTE">
								{{ u.NOME }} {{ u.COGNOME }}
							</option>
						</select>
					</div>

					<!-- Step 2: scelta mese ed elaborazione -->
					<div v-else-if="!selectedElaborazione">
						<div class="row mb-3">
							<div class="col-6">
								<label class="form-label">Mese elaborazione *</label>
								<select v-model="selectedMese" class="form-control" required>
									<option :value="null">Seleziona...</option>
									<option v-for="m in utils.arrayMesi" :key="m.ID_MESE" :value="m.ID_MESE">
										{{ m.DESCRIZIONE }}
									</option>
								</select>
							</div>
							<div class="col-6">
								<label class="form-label">Anno *</label>
								<select v-model="selectedAnno" class="form-control" required>
									<option :value="null">Seleziona...</option>
									<option v-for="a in arrayAnni" :key="a" :value="a">{{ a }}</option>
								</select>
							</div>
						</div>

						<div v-if="elaborazioneNonTrovata" class="alert alert-warning">
							Nessuna elaborazione trovata per {{ formatMeseAnno(selectedMeseData) }}.
						</div>

						<div class="mb-3">
							<button v-if="!elaborazioneNonTrovata" type="button" class="btn btn-primary" :disabled="!selectedMese || !selectedAnno || checkingElaborazione" @click="verificaElaborazione()">
								<span v-if="checkingElaborazione" class="spinner-border spinner-border-sm me-2"></span>
								Continua
							</button>
							<button v-else-if="isAdminView" type="button" class="btn btn-primary" @click="showImportExcel = true">
								Carica elaborazione
							</button>
						</div>
					</div>

					<!-- Step 3: upload/visualizza PDF -->
					<div v-else>
						<p class="text-muted">Carica o sostituisci il PDF unico con la scansione di tutti gli scontrini/fatture per l'elaborazione di <strong>{{ selectedElaborazione.NOME_UTENTE }}</strong> del {{ formatMeseAnno(selectedElaborazione.DATA) }}.</p>

						<div class="mx-auto d-flex flex-column align-items-center mb-3" style="max-width: 540px;">
							<label class="form-label">PDF Elaborazione</label>
							<Upload
								url="core/basic/upload"
								:args="{ modalita: 'THEB_uploadAllegatoElaborazione', ID_ELABORAZIONE: selectedElaborazione.ID_ELABORAZIONE }"
								@onsuccess="onUploadSuccess"
								@onerror="onUploadError"
							/>
							<button v-if="hasFile" type="button" class="btn btn-danger btn-lg mt-2" @click="visualizzaPdf()">
								<i class="ph-file-pdf me-1"></i>Visualizza PDF
							</button>
						</div>

						<button type="button" class="btn btn-sm btn-outline-secondary" @click="cambiaElaborazione()">
							<i class="ph-arrow-left me-1"></i>Cambia elaborazione
						</button>
					</div>

					<div class="modal-footer">
						<button v-if="isAdminView && selectedUtente && !selectedElaborazione" type="button" class="btn btn-sm btn-outline-secondary me-auto" @click="cambiaUtente()">
							<i class="ph-arrow-left me-1"></i>Cambia utente
						</button>
						<button type="button" class="btn btn-secondary" @click="close()">Chiudi</button>
					</div>
				</div>
			</div>
		</div>
	</div>

	<ImportSpeseExcel v-if="showImportExcel"
		:preset-utente="selectedUtente" :preset-data="selectedMeseData"
		@close="showImportExcel = false"
		@imported="onElaborazioneImportata" />
</template>

<script>
import Upload from './utils/Upload.vue';
import ImportSpeseExcel from './ImportSpeseExcel.vue';

export default {
	name: 'ModalUploadPdfElaborazione',
	components: {
		Upload,
		ImportSpeseExcel
	},
	props: {
		isAdminView: {
			type: Boolean,
			default: false
		}
	},
	emits: ['close', 'saved'],
	data() {
		return {
			filters: global.filters,
			utils: global.utils,
			selectedUtente: '',
			selectedMese: null,
			selectedAnno: null,
			selectedElaborazione: null,
			checkingElaborazione: false,
			elaborazioneNonTrovata: false,
			showImportExcel: false
		};
	},
	computed: {
		hasFile() {
			return !!(this.selectedElaborazione && this.selectedElaborazione.FILE_ALLEGATO);
		},
		title() {
			if (!this.selectedElaborazione) return 'Carica PDF Elaborazione';
			return this.hasFile ? 'Sostituisci PDF Elaborazione' : 'Carica PDF Elaborazione';
		},
		arrayAnni() {
			const annoCorrente = new Date().getFullYear();
			const anni = [];
			for (let a = annoCorrente; a >= annoCorrente - 3; a--) {
				anni.push(a);
			}
			return anni;
		},
		selectedMeseData() {
			if (!this.selectedMese || !this.selectedAnno) return null;
			return `${this.selectedAnno}-${String(this.selectedMese).padStart(2, '0')}-01`;
		}
	},
	mounted() {
		if (!this.isAdminView) {
			this.selectedUtente = this.$root.utente.ID_UTENTE;
		}
	},
	methods: {
		formatMeseAnno(dataStr) {
			if (!dataStr) return '';
			const mese = parseInt(dataStr.substring(5, 7), 10);
			const anno = dataStr.substring(0, 4);
			const meseObj = utils.getMese(mese);
			return (meseObj ? meseObj.DESCRIZIONE : '') + ' ' + anno;
		},
		verificaElaborazione() {
			this.checkingElaborazione = true;
			this.elaborazioneNonTrovata = false;
			utils.ajax('theb/elaborazione/verifica-esistente', { FK_ID_UTENTE: this.selectedUtente, DATA: this.selectedMeseData }, (res) => {
				this.checkingElaborazione = false;
				const dati = res.data || {};
				if (dati.trovata) {
					this.selectedElaborazione = dati.elaborazione;
				} else {
					this.elaborazioneNonTrovata = true;
				}
			});
		},
		onElaborazioneImportata() {
			this.showImportExcel = false;
			this.verificaElaborazione();
		},
		cambiaUtente() {
			this.selectedUtente = '';
			this.selectedMese = null;
			this.selectedAnno = null;
			this.elaborazioneNonTrovata = false;
		},
		cambiaElaborazione() {
			this.selectedElaborazione = null;
			this.selectedMese = null;
			this.selectedAnno = null;
			this.elaborazioneNonTrovata = false;
		},
		onUploadSuccess() {
			utils.alert.success('PDF caricato');
			this.$emit('saved');
			this.close();
		},
		onUploadError() {
			utils.alert.warning('Errore upload PDF');
		},
		visualizzaPdf() {
			utils.ajaxBlob('theb/elaborazione/allegato', { ID_ELABORAZIONE: this.selectedElaborazione.ID_ELABORAZIONE }, (blob) => {
				const url = URL.createObjectURL(blob);
				window.open(url, '_blank');
			}, true, 'POST');
		},
		close() {
			this.$emit('close');
		}
	}
};
</script>

<style scoped>
.modal {
	z-index: 1050;
}

.modal.show {
	background-color: rgba(0, 0, 0, 0.5);
}

@media (max-width: 575.98px) {
	.modal-footer {
		flex-direction: column-reverse;
	}
	.modal-footer > * {
		width: 100%;
		margin-left: 0 !important;
		margin-right: 0 !important;
	}
}
</style>
